import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Image as ImageIcon, ArrowUp, ArrowDown, Loader2, X } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const COCKTAIL_CATEGORIES = [
  { id: "signature", label: "Signatures" },
  { id: "rhum_arrange", label: "Rhums Arrangés Primés" },
  { id: "mocktail", label: "Mocktails" },
  { id: "calvados", label: "Cocktails au Calvados" },
  { id: "rhum", label: "Cocktails au Rhum" },
  { id: "spiritueux", label: "Whiskys & Spiritueux" },
  { id: "glace", label: "Cocktails Glacés" },
  { id: "soft", label: "Softs (sans alcool)" },
  { id: "vin_biere", label: "Vins & Bières" },
  { id: "classique", label: "Classiques" },
  { id: "sans_alcool", label: "Sans alcool" },
];

const SNACK_CATEGORIES = [
  { id: "tapas", label: "Tapas / Snacking" },
  { id: "planche", label: "Planches" },
  { id: "dessert", label: "Desserts" },
];

export default function MenuEditor() {
  const [cocktails, setCocktails] = useState([]);
  const [snacks, setSnacks] = useState([]);
  const [activeTab, setActiveTab] = useState("cocktails");
  const [editing, setEditing] = useState(null); // {type, item}
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([api.get("/cocktails"), api.get("/snacks")]);
      setCocktails(c.data);
      setSnacks(s.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const grouped = useMemo(() => {
    const items = activeTab === "cocktails" ? cocktails : snacks;
    const cats = activeTab === "cocktails" ? COCKTAIL_CATEGORIES : SNACK_CATEGORIES;
    return cats.map((cat) => ({
      ...cat,
      items: items.filter((i) => i.category === cat.id).sort((a, b) => (a.display_order ?? 9999) - (b.display_order ?? 9999) || a.name.localeCompare(b.name)),
    })).filter((g) => g.items.length > 0);
  }, [cocktails, snacks, activeTab]);

  const move = async (item, direction) => {
    const list = (activeTab === "cocktails" ? cocktails : snacks).filter((i) => i.category === item.category).sort((a, b) => (a.display_order ?? 9999) - (b.display_order ?? 9999) || a.name.localeCompare(b.name));
    const idx = list.findIndex((i) => i.id === item.id);
    const swap = direction === "up" ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= list.length) return;
    const a = list[idx], b = list[swap];
    // Normalise display_order across the category
    const reordered = [...list];
    [reordered[idx], reordered[swap]] = [reordered[swap], reordered[idx]];
    try {
      await Promise.all(reordered.map((it, i) => save(activeTab, it.id, { ...it, display_order: i }, { silent: true })));
      toast.success(`Ordre mis à jour : ${a.name} ↔ ${b.name}`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const save = async (type, id, payload, { silent } = {}) => {
    const url = type === "cocktails" ? "/admin/cocktails" : "/admin/snacks";
    const clean = {
      name: payload.name,
      description: payload.description,
      price: parseFloat(payload.price),
      category: payload.category,
      image: payload.image || null,
      display_order: payload.display_order ?? null,
    };
    if (id) {
      await api.put(`${url}/${id}`, clean);
    } else {
      await api.post(url, clean);
    }
    if (!silent) toast.success(id ? "Modifications enregistrées" : "Ajout réussi");
  };

  const remove = async (type, item) => {
    if (!window.confirm(`Supprimer "${item.name}" ? Cette action est irréversible.`)) return;
    const url = type === "cocktails" ? "/admin/cocktails" : "/admin/snacks";
    try {
      await api.delete(`${url}/${item.id}`);
      toast.success("Supprimé");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const onSave = async (payload) => {
    try {
      await save(editing.type, editing.item?.id, payload);
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12" data-testid="menu-editor-page">
      <h1 className="font-display text-4xl mb-3">Éditeur de carte</h1>
      <p className="text-sm text-muted-foreground mb-8">
        Renommer, changer la photo, le prix, l&apos;ordre d&apos;affichage de chaque cocktail et planche.
        Le tri public suit l&apos;ordre défini ici (par catégorie).
      </p>

      <div className="flex gap-2 mb-6">
        {["cocktails", "snacks"].map((t) => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={`px-5 py-2 rounded-full text-sm uppercase tracking-[0.15em] border transition-colors ${
              activeTab === t ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card text-muted-foreground hover:border-primary/50"
            }`}
            data-testid={`menu-editor-tab-${t}`}
          >
            {t === "cocktails" ? `Cocktails (${cocktails.length})` : `Snacks / Planches (${snacks.length})`}
          </button>
        ))}
        <Button
          onClick={() => setEditing({ type: activeTab, item: null })}
          className="ml-auto rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="menu-editor-add-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Ajouter
        </Button>
      </div>

      {loading && <div className="text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>}

      {!loading && grouped.map((group) => (
        <div key={group.id} className="mb-10" data-testid={`menu-editor-group-${group.id}`}>
          <h2 className="font-display text-xl text-primary mb-3 uppercase tracking-[0.18em]">{group.label}</h2>
          <div className="space-y-2">
            {group.items.map((item, idx) => (
              <div key={item.id} className="rounded-xl border border-border bg-card p-3 flex items-center gap-3" data-testid={`menu-editor-row-${item.id}`}>
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => move(item, "up")}
                    disabled={idx === 0}
                    className="p-1 rounded hover:bg-primary/20 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Monter"
                    data-testid={`move-up-${item.id}`}
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(item, "down")}
                    disabled={idx === group.items.length - 1}
                    className="p-1 rounded hover:bg-primary/20 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Descendre"
                    data-testid={`move-down-${item.id}`}
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                {item.image ? (
                  <img src={item.image} alt={item.name} className="w-16 h-16 rounded-lg object-cover border border-border shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-input/40 border border-dashed border-border flex items-center justify-center text-muted-foreground shrink-0">
                    <ImageIcon className="w-5 h-5" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{item.name}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2">{item.description}</div>
                </div>
                <div className="font-display text-lg gold-text whitespace-nowrap mr-2">{Number(item.price).toFixed(2)} €</div>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditing({ type: activeTab, item })} data-testid={`edit-${item.id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-1.5" /> Modifier
                </Button>
                <Button size="sm" variant="ghost" className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => remove(activeTab, item)} data-testid={`delete-${item.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      ))}

      {editing && (
        <EditMenuItemModal
          editing={editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
        />
      )}
    </div>
  );
}

function EditMenuItemModal({ editing, onClose, onSave }) {
  const isCocktail = editing.type === "cocktails";
  const cats = isCocktail ? COCKTAIL_CATEGORIES : SNACK_CATEGORIES;
  const [form, setForm] = useState({
    name: editing.item?.name || "",
    description: editing.item?.description || "",
    price: editing.item?.price ?? 0,
    category: editing.item?.category || cats[0].id,
    image: editing.item?.image || "",
    display_order: editing.item?.display_order ?? "",
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, image: fullUrl }));
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Téléversement échoué");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Nom obligatoire"); return; }
    setSaving(true);
    try {
      await onSave({
        ...form,
        display_order: form.display_order === "" ? null : parseInt(form.display_order, 10),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4" onClick={onClose} data-testid="menu-editor-modal">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-card border border-border rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4"
      >
        <div className="flex justify-between items-center mb-2">
          <h2 className="font-display text-2xl">{editing.item ? "Modifier" : "Ajouter"} · {isCocktail ? "Cocktail" : "Snack"}</h2>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-input/40">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid sm:grid-cols-[140px_1fr] gap-4 items-start">
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2 inline-block">Image</Label>
            {form.image ? (
              <div className="relative">
                <img src={form.image} alt="" className="w-[140px] h-[140px] rounded-lg object-cover border border-border" />
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, image: "" }))}
                  className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-destructive text-white flex items-center justify-center"
                  title="Retirer"
                  data-testid="remove-image-btn"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <label className="w-[140px] h-[140px] rounded-lg bg-input/40 border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-primary text-xs text-muted-foreground p-2 text-center" data-testid="upload-image-label">
                <ImageIcon className="w-6 h-6 mb-1" />
                {uploading ? "Envoi…" : "Cliquez pour téléverser"}
                <input type="file" accept="image/*" onChange={onFile} disabled={uploading} className="hidden" data-testid="upload-image-input" />
              </label>
            )}
            {form.image && (
              <label className="text-xs text-primary mt-2 inline-block cursor-pointer hover:underline" data-testid="replace-image-label">
                {uploading ? "Envoi…" : "Remplacer l'image"}
                <input type="file" accept="image/*" onChange={onFile} disabled={uploading} className="hidden" />
              </label>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="me-name" className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1 inline-block">Nom *</Label>
              <Input id="me-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required data-testid="form-name" />
            </div>
            <div>
              <Label htmlFor="me-desc" className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1 inline-block">Description</Label>
              <textarea id="me-desc" rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="w-full rounded-md bg-input border border-border px-3 py-2 text-sm" data-testid="form-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="me-price" className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1 inline-block">Prix (€) *</Label>
                <Input id="me-price" type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} required data-testid="form-price" />
              </div>
              <div>
                <Label htmlFor="me-order" className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1 inline-block">Position</Label>
                <Input id="me-order" type="number" placeholder="Auto" value={form.display_order} onChange={(e) => setForm((f) => ({ ...f, display_order: e.target.value }))} data-testid="form-order" />
              </div>
            </div>
            <div>
              <Label htmlFor="me-cat" className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1 inline-block">Catégorie *</Label>
              <select id="me-cat" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="w-full rounded-md bg-input border border-border px-3 py-2 text-sm" data-testid="form-category">
                {cats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-full">Annuler</Button>
          <Button type="submit" disabled={saving} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="form-submit">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </form>
    </div>
  );
}
